import React, { useMemo } from 'react';
import { EChart, getCompactGrid, getFocusCartesianGrid, getPreviewGrid, isOverviewLikeDensity } from '@/components/visuals';
import type { ChartDensity } from '@/components/visuals';
import type { EChartsOption } from 'echarts';

export interface FloatDistributionChartProps {
  data: Array<{ bucket: string; count: number }>;
  height: number | string;
  onBucketClick: (bucket: string) => void;
  showDataLabels?: boolean;
  density?: ChartDensity;
}

export function FloatDistributionChart({
  data,
  height,
  onBucketClick,
  showDataLabels = true,
  density = 'overview',
}: FloatDistributionChartProps) {
  const compactGrid = getCompactGrid(false);
  const previewGrid = getPreviewGrid(false);
  const option = useMemo<EChartsOption>(() => {
    const labels: Record<string, string> = {
      '<0': '< 0', '0': '0', '1-7': '1–7', '8-14': '8–14',
      '15-30': '15–30', '31-44': '31–44', '>44': '> 44',
    };
    return {
      tooltip: density === 'preview' ? { show: false } : { trigger: 'axis' },
      grid:
        density === 'preview'
          ? previewGrid
          : isOverviewLikeDensity(density)
            ? compactGrid
            : getFocusCartesianGrid(),
      xAxis: {
        type: 'category',
        name: 'Float (days)',
        nameLocation: 'middle',
        nameGap: 32,
        data: data.map((d) => labels[d.bucket] ?? d.bucket),
        axisLabel: { fontSize: density === 'preview' ? 9 : 10 },
      },
      yAxis: {
        type: 'value',
        name: 'Count',
        nameLocation: 'middle',
        nameGap: density === 'preview' ? 28 : 40,
        axisLabel: {
          fontSize: density === 'preview' ? 9 : 10,
          ...(density === 'focus'
            ? { formatter: (v: number) => Number(v).toLocaleString() }
            : {}),
        },
      },
      series: [{
        type: 'bar',
        data: data.map((d) => d.count),
        label: {
          show: density === 'preview' ? false : showDataLabels,
          fontSize: density === 'preview' ? 8 : 10,
          position: 'top',
          distance: 6,
        },
        labelLayout: { hideOverlap: true },
        itemStyle: {
          color: (params: { dataIndex: number }) => {
            const b = data[params.dataIndex]?.bucket;
            if (b === '<0') return '#EF4444';
            if (b === '0') return '#F59E0B';
            return '#3B82F6';
          },
        },
        barMaxWidth: 32,
      }],
    };
  }, [data, showDataLabels, density, compactGrid, previewGrid]);

  const onEvents = useMemo(() => ({
    click: (params: { dataIndex?: number }) => {
      const bucket = data[params.dataIndex ?? 0]?.bucket;
      if (bucket) onBucketClick(bucket);
    },
  }), [data, onBucketClick]);

  return <EChart option={option} height={height} onEvents={onEvents} />;
}
